/* eslint-disable camelcase */
import React, {
  useState,
} from 'react';
import {
  Elements,
  StripeProvider,
} from 'react-stripe-elements';
import {
  Link,
} from 'react-router-dom';
import {
  getStatus,
} from 'redux-resource';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreUtils,
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import config from 'web-config';
import {
  getUiState,
} from 'web-selectors';
import {
  uiActions,
} from 'web-actions';

import {
  Modal,
  Spinner,
} from 'web-components';

import {
  Flex,
  Text,
  H200,
  TextHeavy,
} from 'web-styled';

import Buy from './Buy';
import * as S from './styled';

const isFloat = (n) => Number(n) === n && n % 1 !== 0;
const getAmount = amount => (isFloat(amount)
  ? amount.toFixed(2)
  : amount);

const resourceTypes = [
  'workoutExercises',
  'personalWarmups',
  'personalWorkouts',
  'personalCooldowns',
  'personalPrograms',
];

const mapState = (state) => {
  let items = coreSelectors.getIntents(state);
  const checkoutModal = getUiState('checkoutModal')(state);

  if (checkoutModal.planId) {
    items = [
      coreSelectors.getResourceById(
        'productPlans',
        checkoutModal.planId,
      )(state),
    ];
  }

  return {
    items,
    loadStatus: getStatus(
      state,
      resourceTypes.map(
        r => `${r}.requests.loadResources.status`,
      ),
      false,
    ),
    email: coreSelectors.getProfile('email')(state),
    checkoutModal,
  };
};

type Props = {
  removable?: boolean,
  isUpgrade?: boolean
  fetchAfterPay?: boolean,
  closeAfterFinish?: boolean,
  onFinish?: { (): void }
};

const Checkout = ({
  removable,
  isUpgrade,
  fetchAfterPay,
  closeAfterFinish,
  onFinish,
}: Props) => {
  const {
    email,
    items,
    loadStatus,
    checkoutModal,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const [isFinish, setIsFinish] = useState(false);

  const onRemove = (id) => {
    if (checkoutModal.planId) {
      dispatch(
        uiActions.setUiState({
          checkoutModal: {
            ...checkoutModal,
            planId: null,
          },
        }),
      );
    } else {
      dispatch(
        coreActions.deleteResources({
          resourceType: 'paymentIntents',
          resources: [{
            id,
          }],
        }),
      );
    }
  };

  const onRequestClose = () => {
    dispatch(
      coreActions.deleteResources({
        resourceType: 'paymentIntents',
        resources: items.map(
          i => ({
            id: i.id,
          }),
        ),
      }),
    );
    dispatch(
      uiActions.setUiState({
        checkoutModal: {
          isOpen: false,
        },
      }),
    );
  };

  const loadResources = () => {
    dispatch(
      coreActions.requestFetchResources({
        requestKey: 'loadResources',
        callbacks: {
          onSuccess: () => {
            setIsFinish(true);
          },
        },
      }),
    );
  };

  const handleAfterPay = (intent) => {
    if (isUpgrade) onFinish();
    else if (checkoutModal.planId) loadResources();
    else {
      dispatch(
        coreActions.requestFinishBuy({
          paymentIntentId: intent?.id,
          callbacks: {
            onSuccess: () => {
              if (onFinish) onFinish();
              if (fetchAfterPay) loadResources();
              if (closeAfterFinish) onRequestClose();
            },
          },
        }),
      );
    }
  };
  const total = items.reduce((a, b) => (a + b.amount), 0);

  return (
    <Modal
      title="Checkout"
      isOpen={checkoutModal?.isOpen}
      onRequestClose={onRequestClose}
      contentStyle={{
        width: '580px',
      }}
    >
      {loadStatus.pending && (
        <Spinner
          over
          message="Loading..."
        />
      )}
      <S.Checkout>
        <Flex
          column
          spacing={5}
          width="100%"
        >
          <Flex
            width="100%"
            justify="space-between"
          >
            <Text color="#111">
              Product
            </Text>
            <Text color="#111">
              Price
            </Text>
          </Flex>
          <hr />
          <Flex
            column
            spacing={10}
            width="100%"
          >
            {items.map(item => (
              <S.Item key={item.id}>
                <Flex
                  width="100%"
                  justify="space-between"
                >
                  <Text
                    color="#797979"
                  >
                    {item.name || item.product_data?.name || item.invoice_data?.description}
                  </Text>
                  <Text
                    size="13px"
                  >
                    {coreUtils.getCurrencySymbol(item.currency) || '$'}
                    {getAmount(checkoutModal.planId ? item.amount : (item.amount || 0) / 100)}
                    {checkoutModal.planId && (
                      <small>
                        /
                        {coreUtils.getIntervalABBR(item.interval)}
                      </small>
                    )}
                  </Text>
                </Flex>
                {(removable && !isFinish) && (
                  <S.RemoveItem onClick={() => onRemove(item.id)}>
                    Remove
                  </S.RemoveItem>
                )}
              </S.Item>
            ))}
          </Flex>
          <Flex
            width="100%"
            justify="space-between"
          >
            <TextHeavy color="#111">
              Total
            </TextHeavy>
            <TextHeavy color="#111">
              $
              {getAmount(checkoutModal.planId ? total : total / 100)}
            </TextHeavy>
          </Flex>
        </Flex>
        {isFinish ? (
          <Flex
            column
            spacing={15}
            margin="15px 0"
          >
            <Flex
              column
              spacing={10}
            >
              <H200 color="primary">
                Thank You!
              </H200>
              <Text>
                Order details have been sent to
                {' '}
                <b>
                  {email}
                </b>
                . Your purchased items should now be accessible.
              </Text>
            </Flex>
            <Flex
              width="100%"
              justify="space-between"
            >
              <Link to="/personal_workouts">
                <Text
                  size="15px"
                  color="primary"
                >
                  My Workouts
                </Text>
              </Link>
              <Link to="/personal_programs">
                <Text
                  size="15px"
                  color="primary"
                >
                  My Programs
                </Text>
              </Link>
              <Link to="/account/transactions">
                <Text
                  size="15px"
                  color="primary"
                >
                  Transaction History
                </Text>
              </Link>
            </Flex>
          </Flex>
        ) : (
          <StripeProvider
            apiKey={config.stripe.key}
          >
            <Elements>
              <Buy
                planId={checkoutModal.planId}
                isUpgrade={isUpgrade}
                clientSecret={items[0]?.client_secret}
                onSuccess={handleAfterPay}
              />
            </Elements>
          </StripeProvider>
        )}
      </S.Checkout>
    </Modal>
  );
};

Checkout.defaultProps = {
  removable: true,
  isUpgrade: false,
  fetchAfterPay: false,
  closeAfterFinish: false,
  onFinish: null,
};

export default Checkout;
